import { useEffect, useRef } from "react";
import {
  getSubjectBounds,
  loadImageData,
  maskHasTransparentPixels,
} from "../../utils/getBoundingBox";
import { computeFittedCanvasSize } from "./computeFittedCanvasSize";
import { buildAlphaMaskFromImage, ALPHA_HIT_THRESHOLD } from "./subjectHitTest";
import { tightenLockedSubject } from "./subjectLayout";
import { consumePendingSubjectPlacement } from "./applySubjectPlacement";

export function useSubjectImageLoad(ctx) {
  const {
    allowSoftOverflowForUrlRef,
    alphaMaskFallbackRef,
    alphaMaskRef,
    autoSelectedForUrlRef,
    containerRef,
    creationId,
    cropExtentRef,
    cropStateRef,
    fullFrameShadowRef,
    getTokenRef,
    imageUrl,
    layoutLockFullObjectRef,
    layoutLockRef,
    lockObjectTransform,
    objectPositionRef,
    objectRotationRef,
    objectSizeRef,
    objectTightBoundsRef,
    pendingSubjectPlacementRef,
    onNaturalSize,
    originalImageSizeRef,
    forceFullFramePendingRef,
    preserveLayoutPendingRef,
    preserveLayoutSnapshotRef,
    softOverflow,
    subjectReadyNotifiedUrlRef,
    canvasReady,
    canvasSizeRef,
    imageLoadedRef,
    onCanvasReady,
    onSubjectReadyRef,
    alphaMaskReady,
    canvasSize,
    imageLoaded,
    setAlphaMaskReady,
    setCanvasCornerRadius,
    setCanvasReady,
    setCanvasSize,
    setCropState,
    setImageLoaded,
    setObjectPosition,
    setObjectRotation,
    setObjectSize,
    setOriginalImageSize,
    setSelectedElement,
    setSoftOverflowEnabled,
  } = ctx;

  const retightenSubjectUnderLayoutLock = () => tightenLockedSubject({
    alphaMaskRef,
    alphaMaskFallbackRef,
    originalImageSizeRef,
    layoutLockFullObjectRef,
    objectSizeRef,
    objectPositionRef,
    cropStateRef,
    cropExtentRef,
    objectTightBoundsRef,
    setCropState,
    setObjectSize,
    setObjectPosition,
  });
  // Load image, dimensions, and alpha mask from the same pixels shown on canvas
  useEffect(() => {
    if (!imageUrl) {
      setAlphaMaskReady(false);
      alphaMaskRef.current = null;
      alphaMaskFallbackRef.current = false;
      allowSoftOverflowForUrlRef.current = null;
      setSoftOverflowEnabled(false);
      return;
    }

    setCanvasReady(false);
    setImageLoaded(false);
    if (imageLoadedRef) imageLoadedRef.current = false;
    subjectReadyNotifiedUrlRef.current = null;
    setAlphaMaskReady(false);
    alphaMaskRef.current = null;
    alphaMaskFallbackRef.current = false;
    objectTightBoundsRef.current = false;
    autoSelectedForUrlRef.current = null;

    let cancelled = false;

    const img = new Image();
    if (
      typeof imageUrl === "string" &&
      /^https?:\/\//i.test(imageUrl) &&
      !imageUrl.startsWith("blob:") &&
      !imageUrl.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }

    img.onload = async () => {
      if (cancelled) return;

      const container = containerRef.current;
      if (!container) return;

      const origW = img.naturalWidth || img.width;
      const origH = img.naturalHeight || img.height;
      const aspectRatio = origW / origH;
      try {
        onNaturalSize?.({ width: origW, height: origH });
      } catch {
        // ignore consumer errors
      }

      const fitted = computeFittedCanvasSize(
        container.clientWidth,
        container.clientHeight,
        aspectRatio
      );

      let canvasWidth = fitted?.width ?? origW;
      let canvasHeight = fitted?.height ?? origH;

      if (!fitted) {
        const maxWidth = container.clientWidth * 0.8;
        const maxHeight = container.clientHeight * 0.8;
        if (canvasWidth > maxWidth || canvasHeight > maxHeight) {
          const scale = Math.min(maxWidth / canvasWidth, maxHeight / canvasHeight);
          canvasWidth *= scale;
          canvasHeight *= scale;
        }
      }

      const scaleX = canvasWidth / origW;
      const scaleY = canvasHeight / origH;

      const forceFullFrame = Boolean(forceFullFramePendingRef?.current);
      if (
        !forceFullFrame &&
        !pendingSubjectPlacementRef?.current &&
        layoutLockFullObjectRef.current?.fromSubjectPlacement
      ) {
        layoutLockRef.current = false;
        layoutLockFullObjectRef.current = null;
      }
      if (forceFullFrame) {
        forceFullFramePendingRef.current = false;
        if (pendingSubjectPlacementRef) {
          pendingSubjectPlacementRef.current = null;
        }
        // Drop resize/AI layout lock so canvas AR matches the restored upload.
        layoutLockRef.current = false;
        layoutLockFullObjectRef.current = null;
        allowSoftOverflowForUrlRef.current = null;
        setSoftOverflowEnabled(false);
        if (fullFrameShadowRef.current) fullFrameShadowRef.current = null;
      }

      const preservingLayout =
        !forceFullFrame &&
        preserveLayoutPendingRef.current &&
        preserveLayoutSnapshotRef.current &&
        preserveLayoutSnapshotRef.current.canvasSize?.width > 0 &&
        preserveLayoutSnapshotRef.current.objectSize?.width > 0;

      const keepFullFrame =
        !forceFullFrame && Boolean(softOverflow) && !preservingLayout;
      if (keepFullFrame) {
        fullFrameShadowRef.current = imageUrl;
      } else if (fullFrameShadowRef.current) {
        fullFrameShadowRef.current = null;
      }

      if (preservingLayout) {
        const snap = preserveLayoutSnapshotRef.current;
        canvasWidth = snap.canvasSize.width;
        canvasHeight = snap.canvasSize.height;
        preserveLayoutPendingRef.current = false;
        allowSoftOverflowForUrlRef.current = imageUrl;
        setSoftOverflowEnabled(true);
      } else if (!forceFullFrame && softOverflow) {
        allowSoftOverflowForUrlRef.current = imageUrl;
        setSoftOverflowEnabled(true);
      } else if (allowSoftOverflowForUrlRef.current && allowSoftOverflowForUrlRef.current !== imageUrl) {
        allowSoftOverflowForUrlRef.current = null;
        setSoftOverflowEnabled(false);
      }

      // Recompute canvas to the restored image AR when forcing full-frame.
      if (forceFullFrame) {
        const refit = computeFittedCanvasSize(
          container.clientWidth,
          container.clientHeight,
          aspectRatio
        );
        if (refit) {
          canvasWidth = refit.width;
          canvasHeight = refit.height;
        }
      }

      let nextCrop = { x: 0, y: 0, width: origW, height: origH };
      let nextObjectSize = preservingLayout
        ? { ...preserveLayoutSnapshotRef.current.objectSize }
        : { width: canvasWidth, height: canvasHeight };
      let nextObjectPosition = preservingLayout
        ? { ...preserveLayoutSnapshotRef.current.objectPosition }
        : { x: 0, y: 0 };

      let mask = null;
      let maskError = null;
      try {
        mask = await loadImageData(imageUrl, {
          creationId,
          getToken: () => getTokenRef.current(),
        });
      } catch (loadErr) {
        maskError = loadErr;
        if (creationId) {
          // Never read pixels from a CDN/S3 <img> when we have creationId — CORS taints canvas on AWS.
        } else {
          try {
            mask = buildAlphaMaskFromImage(img);
          } catch (syncErr) {
            maskError = syncErr;
          }
        }
      }

      if (cancelled) return;

      if (mask) {
        alphaMaskRef.current = mask;
        alphaMaskFallbackRef.current = false;

        const hasTransparency = maskHasTransparentPixels(mask, ALPHA_HIT_THRESHOLD);
        if (forceFullFrame) {
          // Uploaded original: always full bitmap on canvas (no opaque crop BB).
          objectTightBoundsRef.current = true;
        } else if (!hasTransparency) {
          console.warn(
            "[CanvasEditor] Cutout has almost no transparent pixels — object/canvas picking will not work. " +
              "Use a true-alpha PNG (production PhotoRoom API key without sandbox_ prefix, or Stability AI)."
          );
          objectTightBoundsRef.current = keepFullFrame;
        } else if (!layoutLockRef.current) {
          // Soft-overflow (AI fringe / CSS shadow) must not skip opaque BB —
          // keepFullFrame only disables clipPath so soft pixels can paint outside.
          const bounds = getSubjectBounds(mask, 0.9);
          const boundsCover =
            bounds &&
            bounds.width * bounds.height >= mask.width * mask.height * 0.97;
          if (boundsCover) {
            console.warn(
              "[CanvasEditor] Could not find a tight subject box — output may be a flat matte or heavily watermarked. " +
                "Use a production PhotoRoom API key (not sandbox_) for best results."
            );
          }
          if (bounds && bounds.width > 0 && bounds.height > 0 && !boundsCover) {
            nextCrop = {
              x: bounds.left,
              y: bounds.top,
              width: bounds.width,
              height: bounds.height,
            };
            if (!preservingLayout) {
              nextObjectSize = {
                width: bounds.width * scaleX,
                height: bounds.height * scaleY,
              };
              nextObjectPosition = {
                x: bounds.left * scaleX,
                y: bounds.top * scaleY,
              };
            }
            objectTightBoundsRef.current = true;
          } else if (keepFullFrame) {
            objectTightBoundsRef.current = true;
          }
        }
      } else {
        alphaMaskRef.current = null;
        alphaMaskFallbackRef.current = true;
        objectTightBoundsRef.current = false;
        console.warn(
          "[CanvasEditor] Alpha mask unavailable — object/canvas picking needs GET /api/background-removal/creations/{id}/image or a blob: cutout URL",
          maskError
        );
      }

      originalImageSizeRef.current = { width: origW, height: origH };
      setOriginalImageSize({ width: origW, height: origH });

      if (layoutLockRef.current && !forceFullFrame && !layoutLockFullObjectRef.current) {
        // Prep handed off a lock before any FILL/FIT baseline existed (fresh editor
        // mount). Laying out unlocked sets canvas size so onSubjectReady can fire;
        // the pending preset then locks with a real baseline.
        layoutLockRef.current = false;
      }

      if (layoutLockRef.current && !forceFullFrame) {
        // Keep locked canvas AR. Restore full FILL/FIT baseline, then hug cutout subject.
        const baseline = layoutLockFullObjectRef.current;
        if (baseline) {
          objectSizeRef.current = {
            width: baseline.width,
            height: baseline.height,
          };
          objectPositionRef.current = { x: baseline.x, y: baseline.y };
          setObjectSize({ width: baseline.width, height: baseline.height });
          setObjectPosition({ x: baseline.x, y: baseline.y });
        }
        const fullCrop = { x: 0, y: 0, width: origW, height: origH };
        cropStateRef.current = fullCrop;
        cropExtentRef.current = { ...fullCrop };
        setCropState(fullCrop);
        objectTightBoundsRef.current = false;
        retightenSubjectUnderLayoutLock();
      } else {
        cropStateRef.current = nextCrop;
        cropExtentRef.current = { ...nextCrop };
        objectSizeRef.current = nextObjectSize;
        objectPositionRef.current = nextObjectPosition;
        setCanvasSize({ width: canvasWidth, height: canvasHeight });
        setCanvasCornerRadius(0);
        setObjectSize(nextObjectSize);
        setObjectPosition(nextObjectPosition);
        setCropState(nextCrop);
      }

      if (cancelled) return;

      if (imageLoadedRef) imageLoadedRef.current = true;
      if (
        pendingSubjectPlacementRef?.current &&
        !pendingSubjectPlacementRef.current.defer
      ) {
        const placementCanvasW = layoutLockRef.current
          ? canvasSizeRef.current.width
          : canvasWidth;
        const placementCanvasH = layoutLockRef.current
          ? canvasSizeRef.current.height
          : canvasHeight;
        if (!layoutLockRef.current) {
          canvasSizeRef.current = {
            width: canvasWidth,
            height: canvasHeight,
          };
        }
        consumePendingSubjectPlacement(
          {
            pendingSubjectPlacementRef,
            canvasSizeRef,
            originalImageSizeRef,
            layoutLockRef,
            layoutLockFullObjectRef,
            cropStateRef,
            cropExtentRef,
            objectSizeRef,
            objectPositionRef,
            objectRotationRef,
            objectTightBoundsRef,
            setCropState,
            setObjectSize,
            setObjectPosition,
            setObjectRotation,
            retighten: retightenSubjectUnderLayoutLock,
          },
          {
            canvasWidth: placementCanvasW,
            canvasHeight: placementCanvasH,
          }
        );
      }

      setAlphaMaskReady(true);
      setImageLoaded(true);
      requestAnimationFrame(() => {
        if (!cancelled) setCanvasReady(true);
      });
    };

    img.onerror = () => {
      if (!cancelled) {
        alphaMaskFallbackRef.current = true;
        setAlphaMaskReady(true);
      }
    };

    img.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [imageUrl, softOverflow]);

  // When creationId arrives after first paint (history sync), build mask + tight layout without reloading image.
  useEffect(() => {
    if (
      !imageUrl ||
      !creationId ||
      !imageLoaded ||
      objectTightBoundsRef.current ||
      layoutLockRef.current ||
      fullFrameShadowRef.current === imageUrl
    ) {
      return;
    }

    const canvasW = canvasSize.width;
    const canvasH = canvasSize.height;
    if (!canvasW || !canvasH) return;

    let cancelled = false;

    (async () => {
      try {
        const mask = await loadImageData(imageUrl, {
          creationId,
          getToken: () => getTokenRef.current(),
        });
        if (cancelled || !mask) return;

        const orig = originalImageSizeRef.current;
        if (!orig.width || !orig.height) return;

        if (!maskHasTransparentPixels(mask, ALPHA_HIT_THRESHOLD)) {
          // Don't retry forever on every canvasSize identity change
          objectTightBoundsRef.current = true;
          return;
        }

        const bounds = getSubjectBounds(mask, 0.9);
        if (
          !bounds ||
          bounds.width <= 0 ||
          bounds.height <= 0 ||
          bounds.width * bounds.height >= mask.width * mask.height * 0.97
        ) {
          objectTightBoundsRef.current = true;
          return;
        }

        // AI shadow preserve-layout: only refresh crop, never move/resize the object.
        if (allowSoftOverflowForUrlRef.current === imageUrl) {
          const nextCrop = {
            x: bounds.left,
            y: bounds.top,
            width: bounds.width,
            height: bounds.height,
          };
          cropStateRef.current = nextCrop;
          cropExtentRef.current = { ...nextCrop };
          objectTightBoundsRef.current = true;
          setCropState(nextCrop);
          return;
        }

        const scaleX = canvasW / orig.width;
        const scaleY = canvasH / orig.height;
        const nextCrop = {
          x: bounds.left,
          y: bounds.top,
          width: bounds.width,
          height: bounds.height,
        };
        const nextObjectSize = {
          width: bounds.width * scaleX,
          height: bounds.height * scaleY,
        };
        const nextObjectPosition = {
          x: bounds.left * scaleX,
          y: bounds.top * scaleY,
        };

        alphaMaskRef.current = mask;
        alphaMaskFallbackRef.current = false;
        objectTightBoundsRef.current = true;
        cropStateRef.current = nextCrop;
        cropExtentRef.current = { ...nextCrop };
        objectSizeRef.current = nextObjectSize;
        objectPositionRef.current = nextObjectPosition;

        setCropState(nextCrop);
        setObjectSize(nextObjectSize);
        setObjectPosition(nextObjectPosition);
        if (!lockObjectTransform) {
          setSelectedElement("object");
        }
      } catch (err) {
        console.warn("[CanvasEditor] Mask layout retry failed", err);
        // Avoid request storm if creation image fetch fails
        objectTightBoundsRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    creationId,
    imageUrl,
    imageLoaded,
    canvasSize.width,
    canvasSize.height,
    lockObjectTransform,
  ]);

  useEffect(() => {
    if (!imageLoaded || !imageUrl || !alphaMaskReady) {
      return;
    }
    if (lockObjectTransform) {
      setSelectedElement(null);
      return;
    }
    if (autoSelectedForUrlRef.current === imageUrl) {
      return;
    }
    autoSelectedForUrlRef.current = imageUrl;
    setSelectedElement("object");
    // Do not call onObjectClick here — that forces the Object panel and fights
    // tool-prep Done flows that intentionally open Background/Resize/etc.
  }, [imageLoaded, imageUrl, alphaMaskReady, lockObjectTransform]);

  useEffect(() => {
    if (!imageUrl || !imageLoaded || !canvasReady) return;
    const size = canvasSizeRef.current;
    if (!size.width || !size.height) return;
    if (subjectReadyNotifiedUrlRef.current === imageUrl) return;

    subjectReadyNotifiedUrlRef.current = imageUrl;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        onSubjectReadyRef.current?.();
      });
    });
  }, [imageUrl, imageLoaded, canvasReady, canvasSize.width, canvasSize.height]);

  const onCanvasReadyRef = useRef(onCanvasReady);
  onCanvasReadyRef.current = onCanvasReady;

  useEffect(() => {
    if (!imageLoaded || !onCanvasReadyRef.current) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;
    let raf2 = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        timeoutId = setTimeout(() => {
          if (!cancelled) {
            onCanvasReadyRef.current?.();
          }
        }, 100);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      if (raf2 != null) cancelAnimationFrame(raf2);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [imageLoaded]);
}
